import React, { useEffect, useState } from 'react';
import { getGenerationDetail, AdminGenerationDetail, generationStatusPillClass, formatMicroUsd } from '../../lib/adminApi';
import { formatDate } from '../../lib/date';

function ImageCard({ label, url }: { label: string; url: string | null }) {
  return (
    <div className="gen-preview-image-card">
      {url ? (
        <img src={url} alt={label} />
      ) : (
        <div className="gen-preview-image-placeholder">No image</div>
      )}
      <div className="gen-preview-image-label">{label}</div>
    </div>
  );
}

function GenerationPreviewModal({
  generationId,
  onClose,
}: {
  generationId: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<AdminGenerationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setDetail(null);
    getGenerationDetail(generationId)
      .then((result) => {
        if (!cancelled) setDetail(result);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load generation.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [generationId]);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal-lg" style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <button className="gen-preview-close" onClick={onClose} aria-label="Close">×</button>
        <h2>Generation Preview</h2>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <div className="spinner" />
          </div>
        )}

        {error && (
          <div className="alert alert-error" role="alert">{error}</div>
        )}

        {!loading && !error && detail && (
          <>
            {detail.status === 'FAILED' && detail.errorMessage && (
              <div className="gen-preview-error-banner" role="alert">
                <strong>Error:</strong> {detail.errorMessage}
              </div>
            )}

            <div className="gen-preview-meta">
              <span><strong>ID:</strong> {detail.id}</span>
              <span><strong>User:</strong> {detail.userEmail}</span>
              <span>
                <strong>Status:</strong>{' '}
                <span className={`admin-pill admin-pill-${generationStatusPillClass(detail.status)}`}>
                  {detail.status}
                </span>
              </span>
              <span><strong>Provider:</strong> {detail.aiProvider}</span>
              <span><strong>Tokens:</strong> {detail.totalTokens}</span>
              <span><strong>Cost:</strong> {formatMicroUsd(detail.costMicroUsd)}</span>
              <span><strong>Date:</strong> {formatDate(detail.createdAt)}</span>
            </div>

            <div className="gen-preview-section">
              <h3>Inputs</h3>
              <div className="gen-preview-images">
                <ImageCard label="Room" url={detail.roomImageUrl} />
                <ImageCard label="Product" url={detail.productImageUrl} />
                {detail.referenceImageUrls.map((url, i) => (
                  <ImageCard key={url} label={`Reference ${i + 1}`} url={url} />
                ))}
              </div>
            </div>

            <div className="gen-preview-section">
              <h3>Result</h3>
              <div className="gen-preview-images">
                <ImageCard label="Result" url={detail.resultImageUrl} />
              </div>
            </div>

            <div className="gen-preview-section">
              <h3>Prompt</h3>
              <div className="gen-preview-text-block">
                <strong>Product Description:</strong><br />{detail.productDescription || '—'}
              </div>
              <div className="gen-preview-text-block">
                <strong>User Instructions:</strong><br />{detail.userInstructions || '—'}
              </div>
              <div className="gen-preview-text-block">
                <strong>Generated Prompt:</strong><br />{detail.generatedPrompt || '—'}
              </div>
            </div>

            {detail.tokenUsage.length > 0 && (
              <div className="gen-preview-section">
                <h3>Token Breakdown</h3>
                <div className="admin-table-wrap" style={{ marginBottom: 0 }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Phase</th>
                        <th>Model</th>
                        <th>Prompt</th>
                        <th>Candidate</th>
                        <th>Total</th>
                        <th>Cost (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.tokenUsage.map((t, i) => (
                        <tr key={`${t.phase}-${i}`}>
                          <td>{t.phase}</td>
                          <td>{t.model}</td>
                          <td>{t.promptTokens}</td>
                          <td>{t.candidateTokens}</td>
                          <td>{t.totalTokens}</td>
                          <td>{formatMicroUsd(t.costMicroUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default GenerationPreviewModal;
